import {
	UniversalLogger,
	type LogEntry,
	type LogLevel,
	type LoggerConfig,
} from '@monorepo/shared-utils';

/**
 * Production-ready logging utility
 * Provides structured logging with different levels and environment-aware output
 * Extends the shared UniversalLogger with frontend-specific buffering and remote logging
 */

// Extend Window interface to include session store
declare global {
	interface Window {
		__SESSION_STORE__?: {
			sessionId: string;
		};
	}
}

export type { LogLevel, LogEntry, LoggerConfig };

class FrontendLogger extends UniversalLogger {
	private buffer: LogEntry[] = [];
	private flushTimer: number | null = null;
	private enableRemote: boolean;
	private remoteEndpoint?: string;
	private readonly BUFFER_SIZE = 100;
	private readonly FLUSH_INTERVAL = 30000;

	constructor() {
		const enableRemote = import.meta.env.PROD && !!import.meta.env.VITE_LOG_ENDPOINT;

		super({
			level: FrontendLogger.getLogLevel(),
			enableConsole: import.meta.env.DEV || import.meta.env.VITE_ENABLE_CONSOLE_LOGS === 'true',
		});

		// Silence console logs in production if configured
		// Logic removed as we rely on VITE_LOG_LEVEL now
		// if (import.meta.env.PROD && import.meta.env.VITE_SILENCE_CONSOLE_LOGS === 'true') {
		// 	this.silenceConsole();
		// }

		this.enableRemote = enableRemote;
		this.remoteEndpoint = import.meta.env.VITE_LOG_ENDPOINT;

		if (this.enableRemote) {
			this.startFlushTimer();
		}
	}

	private static getLogLevel(): LogLevel {
		const level = import.meta.env.VITE_LOG_LEVEL?.toLowerCase();
		if (level && ['debug', 'info', 'warn', 'error'].includes(level)) {
			return level as LogLevel;
		}
		return import.meta.env.DEV ? 'debug' : 'warn';
	}

	protected override onLogEntry(entry: LogEntry): void {
		// Run standard hook if needed
		super.onLogEntry(entry);

		if (typeof window !== 'undefined' && window.__SESSION_STORE__?.sessionId) {
			entry.sessionId = window.__SESSION_STORE__.sessionId;
		}

		if (this.enableRemote) {
			this.buffer.push(entry);
			if (this.buffer.length >= this.BUFFER_SIZE) this.flush();
		}
	}

	private startFlushTimer(): void {
		if (this.flushTimer || typeof window === 'undefined') return;

		this.flushTimer = window.setInterval(() => {
			if (this.buffer.length > 0) {
				this.flush();
			}
		}, this.FLUSH_INTERVAL);
	}

	private async flush(): Promise<void> {
		if (this.buffer.length === 0 || !this.enableRemote || !this.remoteEndpoint) {
			return;
		}

		// const entries = [...this.buffer];
		this.buffer = [];

		try {
			// NOTE: Logging endpoint needs to be added to API contract
			// For now, use fetch directly. This will likely fail gracefully if endpoint doesn't exist.
			console.warn('[Logger] Remote logging endpoint not yet implemented in contract-first API');
			// await fetch(this.remoteEndpoint, { method: 'POST', body: JSON.stringify({ logs: entries }) });
		} catch (error) {
			if (import.meta.env.DEV) {
				console.warn('Failed to send logs to remote endpoint:', error);
			}
			// Re-queue or drop? For now drop to avoid memory leak loop
		}
	}

	/**
	 * Silences standard console output for production
	 * Preserves Warn and Error
	 */
	// private silenceConsole(): void { ... } - REMOVED

	// Convenience methods specific to Frontend/Analytics

	sessionEvent(event: string, context?: Record<string, unknown>): void {
		this.info(`Session: ${event}`, context);
	}

	apiCall(method: string, endpoint: string, context?: Record<string, unknown>): void {
		this.debug(`API: ${method} ${endpoint}`, context);
	}

	userAction(action: string, context?: Record<string, unknown>): void {
		this.info(`User: ${action}`, context);
	}

	performanceMetric(metric: string, value: number, context?: Record<string, unknown>): void {
		this.info(`Performance: ${metric}`, { value, ...context });
	}

	destroy(): void {
		if (this.flushTimer) {
			clearInterval(this.flushTimer);
			this.flushTimer = null;
		}
		if (this.buffer.length > 0) {
			this.flush();
		}
	}
}

// Create singleton instance
const logger = new FrontendLogger();

// Export both the class and the singleton
export { FrontendLogger as Logger };
export default logger;

// Convenience exports for direct use, bound to the singleton
export const debug = logger.debug.bind(logger);
export const info = logger.info.bind(logger);
export const warn = logger.warn.bind(logger);
export const error = logger.error.bind(logger);
export const sessionEvent = logger.sessionEvent.bind(logger);
export const apiCall = logger.apiCall.bind(logger);
export const userAction = logger.userAction.bind(logger);
export const performanceMetric = logger.performanceMetric.bind(logger);
